import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';

const MostPopularOne = () => {
    const [categories, setCategories] = useState([]);
    const [mostPopularProducts, setMostPopularProducts] = useState([]);
    const [filteredProducts, setFilteredProducts] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [loading, setLoading] = useState(true);
    const scrollContainerRef = useRef(null);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const categoriesResponse = await axios.get(`${process.env.REACT_APP_API_URL}/categories/`);
                const mostPopularResponse = await axios.get(`${process.env.REACT_APP_API_URL}/most-popular/`);

                setCategories(categoriesResponse.data);
                setMostPopularProducts(mostPopularResponse.data);

                if (categoriesResponse.data.length > 0) {
                    setSelectedCategory(categoriesResponse.data[0]);
                    filterMostPopularByCategory(categoriesResponse.data[0], mostPopularResponse.data);
                }

                setLoading(false);
            } catch (error) {
                console.error('Error fetching data:', error);
                setLoading(false);
            }
        };

        fetchData();

        // HIGHLIGHT PRODUCT IMAGES - BORDERLESS CARDS
        const styleSheet = document.createElement("style");
        styleSheet.textContent = `
            .recommended .product-card {
                border: 2px solid #299E60 !important;
                background: #f9fcfb !important;
                padding: 10px !important; /* Minimal padding for wide look */
                border-radius: 24px !important;
                display: flex !important;
                flex-direction: column !important;
                height: auto !important;
                transition: all 0.5s cubic-bezier(0.165, 0.84, 0.44, 1) !important;
                box-shadow: 0 8px 25px rgba(41, 158, 96, 0.12) !important;
                overflow: hidden !important;
            }
            .recommended .product-card:hover {
                transform: translateY(-5px) !important;
                box-shadow: 0 12px 35px rgba(41, 158, 96, 0.2) !important;
                background: #ffffff !important;
                border-color: #35c677 !important;
            }
            .recommended .product-card__thumb {
                width: 100% !important;
                height: 180px !important; /* Shorter, horizontal focal point */
                border-radius: 16px !important;
                background: #fff !important;
                display: flex !important;
                align-items: center !important;
                justify-content: center !important;
                margin-bottom: 12px !important;
                overflow: hidden !important;
                border: none !important;
            }
            .recommended .product-card__thumb img {
                width: 100% !important; /* Force horizontal span */
                height: 100% !important; /* Fill thumb height */
                object-fit: cover !important; /* Cinematic wide crop style */
                transition: transform 0.6s cubic-bezier(0.165, 0.84, 0.44, 1) !important;
            }
            .recommended .product-card__content {
                text-align: center !important;
                padding: 10px 5px !important;
                display: flex !important;
                flex-direction: column !important;
                align-items: center !important; /* Force items to center of the column */
                justify-content: center !important;
                width: 100% !important;
            }
            .recommended .product-card__cart {
                display: inline-flex !important; /* Use inline-flex for fit-content width */
                align-items: center !important;
                justify-content: center !important;
                margin: 10px auto 0 !important; /* High-priority auto margin centering */
                width: fit-content !important; /* Override w-100 */
                min-width: 140px !important;
                background: linear-gradient(90deg, #25a163 0%, #30c075 100%) !important;
                color: #fff !important;
                font-weight: 700 !important;
                letter-spacing: 0.5px !important;
                border-radius: 12px !important;
                padding: 12px 24px !important;
                box-shadow: 0 4px 12px rgba(37, 161, 99, 0.2) !important;
                transition: all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275) !important;
                text-decoration: none !important;
                border: 1px solid rgba(255, 255, 255, 0.1) !important;
            }
            .recommended .product-card__cart:hover {
                background: linear-gradient(90deg, #1e8551 0%, #25a163 100%) !important;
                color: #fff !important;
                transform: scale(1.05) translateY(-2px) !important;
                box-shadow: 0 6px 15px rgba(37, 161, 99, 0.3) !important;
            }
            .recommended .product-card__content h6.title {
                font-weight: 800 !important;
                font-size: 1.15rem !important;
                margin-bottom: 12px !important;
                color: #000000 !important; /* Forced black color */
            }
            .recommended .product-card__content h6.title .link {
                color: #000000 !important; /* Ensure inner link is also black */
                text-decoration: none !important;
            }
            .recommended .product-card__content h6.title .link:hover {
                color: #299E60 !important; /* Theme green on hover for feedback */
            }
            .recommended .product-card__price {
                margin-top: 5px !important;
                font-size: 1.05rem !important;
                font-weight: 700 !important;
                color: #299E60 !important;
                width: 100% !important;
                text-align: center !important;
            }
        `;
        document.head.appendChild(styleSheet);
        return () => {
            if (document.head.contains(styleSheet)) {
                document.head.removeChild(styleSheet);
            }
        };
    }, []);

    const filterMostPopularByCategory = (category, popularData = mostPopularProducts) => {
        const categoryMaterialIds = new Set();

        if (category.product_types && category.product_types.length > 0) {
            category.product_types.forEach(productType => {
                if (productType.materials && productType.materials.length > 0) {
                    productType.materials.forEach(material => {
                        categoryMaterialIds.add(material.id);
                    });
                }
            });
        }

        const filtered = popularData.filter(product =>
            categoryMaterialIds.has(product.material)
        );

        setFilteredProducts(filtered);

        // Reset scroll position to start
        if (scrollContainerRef.current) {
            scrollContainerRef.current.scrollLeft = 0;
        }
    };

    const handleCategoryClick = (category) => {
        setSelectedCategory(category);
        filterMostPopularByCategory(category);
    };

    const handlePrev = () => {
        if (scrollContainerRef.current) {
            const cardWidth = 280; // Card width + gap
            const scrollAmount = cardWidth + 20; // Card + gap
            scrollContainerRef.current.scrollBy({
                left: -scrollAmount,
                behavior: 'smooth'
            });
        }
    };

    const handleNext = () => {
        if (scrollContainerRef.current) {
            const cardWidth = 280; // Card width + gap
            const scrollAmount = cardWidth + 20; // Card + gap
            scrollContainerRef.current.scrollBy({
                left: scrollAmount,
                behavior: 'smooth'
            });
        }
    };

    const getBadgeClass = (badgeColor) => {
        const colorMap = {
            'info': 'bg-info-600',
            'danger': 'bg-danger-600',
            'warning': 'bg-warning-600',
            'success': 'bg-success-600',
            'primary': 'bg-primary-600'
        };
        return colorMap[badgeColor] || 'bg-primary-600';
    };

    if (loading) {
        return (
            <section className="recommended">
                <div className="container container-lg">
                    <div className="text-center py-5">
                        <div className="spinner-border text-main-600" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                    </div>
                </div>
            </section>
        );
    }

    if (categories.length === 0) return null;

    return (
        <section className="recommended">
            <div className="container container-lg">
                {/* Header with Navigation Buttons */}
                <div className="section-heading-with-nav">
                    <div className="section-heading-left">
                        <h5 className="mb-0">Most Popular</h5>
                    </div>

                    <div className="flex-align gap-16">
                        {/* <Link
                            to="#"
                            className="text-sm fw-medium text-gray-700 hover-text-main-600 hover-text-decoration-underline view-all-responsive"
                        >
                            View All Deals
                        </Link> */}
                        {/* Custom Navigation Buttons */}
                        <div className="flex-align gap-8">
                            <button
                                onClick={handlePrev}
                                className="w-40 h-40 flex-center rounded-circle border border-gray-100 hover-border-main-600 hover-bg-main-600 hover-text-white transition-1"
                                aria-label="Previous"
                                disabled={filteredProducts.length === 0}
                            >
                                <i className="ph ph-caret-left text-xl" />
                            </button>
                            <button
                                onClick={handleNext}
                                className="w-40 h-40 flex-center rounded-circle border border-gray-100 hover-border-main-600 hover-bg-main-600 hover-text-white transition-1"
                                aria-label="Next"
                                disabled={filteredProducts.length === 0}
                            >
                                <i className="ph ph-caret-right text-xl" />
                            </button>
                        </div>
                    </div>
                </div>
                {/* Category Tabs */}
                <div className="category-tabs-wrapper">
                    <ul className="nav common-tab nav-pills" role="tablist">
                        {categories.map((category) => (
                            <li className="nav-item" key={category.id} role="presentation">
                                <button
                                    className={`nav-link ${selectedCategory?.id === category.id ? 'active' : ''}`}
                                    onClick={() => handleCategoryClick(category)}
                                    type="button"
                                >
                                    {category.name}
                                </button>
                            </li>
                        ))}
                    </ul>
                </div>

                {/* Products Carousel - Horizontal Scroll */}
                <div className="products-carousel-wrapper">
                    <motion.div
                        key={selectedCategory?.id || 'initial'}
                        className="products-grid"
                        ref={scrollContainerRef}
                        initial="hidden"
                        animate="show"
                        variants={{
                            hidden: { opacity: 0 },
                            show: {
                                opacity: 1,
                                transition: {
                                    staggerChildren: 0.1
                                }
                            }
                        }}
                    >
                        {filteredProducts.length > 0 ? (
                            filteredProducts.map((product) => {
                                const materialData = product.material_data || {};
                                const originalPrice = parseFloat(materialData.material_cost_per_sqft || 0);
                                const salePercentage = product.sale_percentage || 0;
                                const discountedPrice = salePercentage > 0
                                    ? (originalPrice * (1 - salePercentage / 100)).toFixed(2)
                                    : originalPrice.toFixed(2);

                                return (
                                    <motion.div
                                        className="product-grid-item"
                                        key={product.id}
                                        variants={{
                                            hidden: { opacity: 0, scale: 0.9, y: 20 },
                                            show: { opacity: 1, scale: 1, y: 0 }
                                        }}
                                        transition={{ type: "spring", stiffness: 100 }}
                                    >
                                        <div className="product-card h-100 p-8 position-relative transition-2">
                                            {/* Sale Badge */}
                                            {salePercentage > 0 && (
                                                <span className="product-card__badge bg-danger-600 px-8 py-4 text-sm text-white">
                                                    Sale {salePercentage}%
                                                </span>
                                            )}

                                            {!salePercentage && product.badge && (
                                                <span className={`product-card__badge ${getBadgeClass(product.badge_color)} px-8 py-4 text-sm text-white`}>
                                                    {product.badge}
                                                </span>
                                            )}

                                            <Link
                                                to={`/product-details-material?type=${materialData.product_type}&material=${product.material}`}
                                                className="product-card__thumb flex-center"
                                            >
                                                <img
                                                    src={materialData.swatch_url || 'assets/images/thumbs/default-product.jpg'}
                                                    alt={materialData.name || product.title}
                                                    onError={(e) => e.target.src = 'assets/images/thumbs/default-product.jpg'}
                                                />
                                            </Link>

                                            <div className="product-card__content p-sm-2 text-center">


                                                <h6 className="title text-lg fw-semibold mb-8">
                                                    <Link
                                                        to={`/product-details-material?type=${materialData.product_type}&material=${product.material}`}
                                                        className="link text-line-2"
                                                    >
                                                        {materialData.name || product.title}
                                                    </Link>
                                                </h6>

                                                <div className="product-card__price mb-12">
                                                    {salePercentage > 0 ? (
                                                        <div className="d-flex align-items-center gap-8 flex-wrap justify-content-center">
                                                            <span className="text-heading text-md fw-semibold">
                                                                ${discountedPrice}{" "}
                                                                <span className="text-gray-500 fw-normal">/sqft</span>
                                                            </span>
                                                            <span className="text-gray-400 text-sm fw-semibold text-decoration-line-through">
                                                                ${originalPrice.toFixed(2)}
                                                            </span>
                                                        </div>
                                                    ) : (
                                                        <span className="text-heading text-md fw-semibold">
                                                            ${originalPrice.toFixed(2)}{" "}
                                                            <span className="text-gray-500 fw-normal">/sqft</span>
                                                        </span>
                                                    )}
                                                </div>

                                                <div className="flex-align gap-6 mb-16 justify-content-center">
                                                    {[...Array(5)].map((_, i) => (
                                                        <span key={i} className="text-15 fw-medium text-warning-600 d-flex">
                                                            <i className="ph-fill ph-star" />
                                                        </span>
                                                    ))}
                                                    <span className="text-13 text-gray-500 fw-medium ms-4">(4.8)</span>
                                                </div>

                                                <Link
                                                    to={`/product-details-material?type=${materialData.product_type}&material=${product.material}`}
                                                    className="product-card__cart flex-align gap-8 w-100 justify-content-center"
                                                >
                                                    Customize <i className="ph ph-magic-wand" />
                                                </Link>
                                            </div>
                                        </div>
                                    </motion.div>
                                );
                            })
                        ) : (
                            <div className="text-center py-5 w-100">
                                <p className="text-gray-500">No popular products in this category</p>
                            </div>
                        )}
                    </motion.div>
                </div>
            </div>
        </section>
    );
};

export default MostPopularOne;
